import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useResponsive } from "./responsive";
import { useTranslation } from "../i18n";

// Mirrors DEFAULT_REACTION_KEYS in backend/app/services/reactions/catalog.py.
// Labels are catalog keys, resolved with t(labelKey) inside the component.
const DEFAULT_REACTIONS = [
  { key: "thumbs_up", emoji: "👍", labelKey: "reactions.thumbs_up" },
  { key: "heart", emoji: "❤️", labelKey: "reactions.heart" },
  { key: "laugh", emoji: "😆", labelKey: "reactions.laugh" },
  { key: "surprised", emoji: "😮", labelKey: "reactions.wow" },
  { key: "tada", emoji: "🎉", labelKey: "reactions.celebrate" },
  { key: "clap", emoji: "👏", labelKey: "reactions.clap" },
];

const ReactionPicker = ({ catalog, reactions, onSelect, onClose, anchorRef }) => {
  const { t } = useTranslation("feed");
  const { isMobile } = useResponsive();
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (isMobile) return undefined;
    const place = () => {
      const trigger = anchorRef?.current;
      const el = popRef.current;
      if (!trigger || !el) return;
      const t = trigger.getBoundingClientRect();
      const margin = 8;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      // Right-align to the trigger; prefer above, flip below when out of room.
      const left = Math.min(
        Math.max(t.right - w, margin),
        Math.max(window.innerWidth - w - margin, margin)
      );
      let top = t.top - h - margin;
      if (top < margin) top = Math.min(t.bottom + margin, window.innerHeight - h - margin);
      setPos({ left, top });
    };
    place();
    // Stay glued to the trigger while the page scrolls or resizes.
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isMobile, catalog, anchorRef]);

  const categories = useMemo(() => {
    if (!catalog?.options) return [];
    const grouped = {};
    catalog.options.forEach((o) => {
      (grouped[o.category] = grouped[o.category] || []).push(o);
    });
    return Object.entries(grouped);
  }, [catalog]);

  const optionClass = (key, size) =>
    `${size} rounded-lg p-1 hover:bg-gray-100 transition-colors ${
      reactions[key]?.reacted ? "bg-purple-50" : ""
    }`;

  const body = (
    <>
      <div className="flex items-center gap-1 pb-2 mb-2 border-b border-gray-100">
        {DEFAULT_REACTIONS.map(({ key, emoji, labelKey }) => (
          <button
            key={key}
            type="button"
            title={t(labelKey)}
            aria-label={t(labelKey)}
            onClick={() => onSelect(key, emoji)}
            className={`${optionClass(key, "text-xl")} hover:scale-125 transition-transform`}
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="max-h-52 overflow-y-auto">
        {!catalog ? (
          <p className="text-sm text-gray-500 text-center py-4">{t("reactions.loading")}</p>
        ) : (
          categories.map(([category, options]) => (
            <div key={category} className="mb-2 last:mb-0">
              <div className="text-xs font-medium text-slate-500 mb-1">
                {category}
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-6 gap-1">
                {options.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    title={o.label}
                    aria-label={o.label}
                    onClick={() => onSelect(o.key, o.emoji)}
                    className={optionClass(o.key, "text-lg")}
                  >
                    {o.emoji}
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  if (isMobile) {
    return createPortal(
      <div
        className="reaction-picker-root fixed inset-0 z-[100] bg-black/40 flex items-end menu-backdrop-in"
        onClick={onClose}
      >
        <div
          className="w-full bg-white rounded-t-2xl shadow-xl p-3 pb-4 border-t border-gray-200 max-h-[70vh] overflow-hidden menu-sheet-in"
          style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center pt-0.5 pb-2">
            <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">{t("reactions.pick")}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("reactions.close")}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {body}
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="reaction-picker-root fixed inset-0 z-[100]" onClick={onClose}>
      <div
        ref={popRef}
        className="fixed bg-white border border-gray-200 rounded-2xl shadow-lg w-72 p-3 menu-pop-in"
        style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
        onClick={(e) => e.stopPropagation()}
      >
        {body}
      </div>
    </div>,
    document.body
  );
};

export default ReactionPicker;
